import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { DEFAULT_CURRENCY, QUERY_KEYS } from '@/constants';
import type { TyGiaQuyDoiNgoaiTe } from '@/types/schemas';
import { FormFieldV2 } from '../form-field-v2';
import { Label } from '@/components/ui/label';
import type { FormMode } from '@/types/form';
import { useCRUD } from '@/hooks';
import { cn } from '@/lib/utils';

export interface CurrencyInputV2Props {
  formMode: FormMode;
  disabled?: boolean;
  labelClassName?: string;
  className?: string;
  classNameInput?: string;
  currencyFieldName?: string;
  exchangeRateFieldName?: string;
  autoMapExchangeRate?: boolean;
  dateFieldName?: string;
  onExchangeRateChange?: (exchangeRate: number) => void;
  onCurrencyChange?: (currencyUuid: string | null) => void;
  defaultCurrency?: string;
}

export function CurrencyInputV2({
  formMode,
  disabled = false,
  labelClassName = 'w-32 shrink-0',
  className = 'flex items-center gap-3',
  classNameInput = 'min-w-0 flex-1',
  currencyFieldName = 'ma_nt',
  exchangeRateFieldName = 'ty_gia',
  autoMapExchangeRate = true,
  dateFieldName = 'ngay_ct',
  onExchangeRateChange,
  onCurrencyChange,
  defaultCurrency = DEFAULT_CURRENCY
}: CurrencyInputV2Props) {
  const { data } = useCRUD<TyGiaQuyDoiNgoaiTe, any>({ endpoint: QUERY_KEYS.TY_GIA_QUY_DOI_NGOAI_TE });

  const exchangeRates = useMemo(
    () =>
      data.map(item => ({
        value: item.ma_nt,
        label: item.ma_nt_data?.ma_nt ?? item.ma_nt
      })),
    [data]
  );

  const exchangeRateByCurrency = useMemo(
    () =>
      data.reduce<Record<string, number | undefined>>((accumulator, item) => {
        accumulator[item.ma_nt] = item.ty_gia != null ? Number(item.ty_gia) : undefined;
        return accumulator;
      }, {}),
    [data]
  );

  const { getValues, setValue, watch } = useFormContext<any>();
  const selectedCurrency = watch(currencyFieldName);
  const exchangeRate = watch(exchangeRateFieldName);
  const onExchangeRateChangeRef = useRef(onExchangeRateChange);
  const onCurrencyChangeRef = useRef(onCurrencyChange);
  const lastEmittedCurrencyRef = useRef<string | null>(selectedCurrency ?? null);
  const lastEmittedExchangeRateRef = useRef<number | undefined>(
    exchangeRate == null || exchangeRate === '' ? undefined : Number(exchangeRate)
  );

  useEffect(() => {
    onExchangeRateChangeRef.current = onExchangeRateChange;
  }, [onExchangeRateChange]);

  useEffect(() => {
    onCurrencyChangeRef.current = onCurrencyChange;
  }, [onCurrencyChange]);

  useEffect(() => {
    if (
      !selectedCurrency ||
      (exchangeRates.length > 0 && !exchangeRates.some(rate => rate.value === selectedCurrency))
    ) {
      const defaultCurrencyOption = exchangeRates.find(rate => rate.label === defaultCurrency);
      const defaultCurrencyValue = defaultCurrencyOption?.value || exchangeRates[0]?.value || null;

      if (defaultCurrencyValue && getValues(currencyFieldName) !== defaultCurrencyValue) {
        setValue(currencyFieldName, defaultCurrencyValue, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false
        });
      }
    }
  }, [currencyFieldName, defaultCurrency, exchangeRates, getValues, selectedCurrency, setValue]);

  const getExchangeRateValue = () => {
    if (!autoMapExchangeRate || !selectedCurrency) {
      return undefined;
    }

    return exchangeRateByCurrency[selectedCurrency];
  };

  useEffect(() => {
    const normalizedRate = getExchangeRateValue();
    const currentRate = exchangeRate == null || exchangeRate === '' ? undefined : Number(exchangeRate);

    if (normalizedRate != null && normalizedRate !== currentRate) {
      setValue(exchangeRateFieldName, normalizedRate, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false
      });
    }

    if (normalizedRate != null && lastEmittedExchangeRateRef.current !== normalizedRate) {
      lastEmittedExchangeRateRef.current = normalizedRate;
      onExchangeRateChangeRef.current?.(normalizedRate);
    }
  }, [autoMapExchangeRate, exchangeRate, exchangeRateByCurrency, exchangeRateFieldName, selectedCurrency, setValue]);

  useEffect(() => {
    const nextCurrency = selectedCurrency ?? null;

    if (lastEmittedCurrencyRef.current !== nextCurrency) {
      lastEmittedCurrencyRef.current = nextCurrency;
      onCurrencyChangeRef.current?.(nextCurrency);
    }
  }, [selectedCurrency]);

  const isDisabled = formMode === 'view' || disabled;

  return (
    <div className={className}>
      <Label className={labelClassName}>Ngoại tệ</Label>

      <FormFieldV2
        name={currencyFieldName}
        type='select'
        disabled={isDisabled}
        options={exchangeRates as { value: string; label: string }[]}
        className='w-[100px] shrink-0'
      />

      <FormFieldV2
        name={exchangeRateFieldName}
        type='number-formatted'
        disabled={isDisabled}
        className={cn(classNameInput)}
      />
    </div>
  );
}
